import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { billingQueries } from 'src/services/billing/queries'
import { OrgWalletSummary } from 'src/services/billing/models'
import { useUser } from 'src/services/user'

export type BillableAiFeature = 'ai_receipt_scanning' | 'ai_statement_reconciliation'

const featureFlag: Record<BillableAiFeature, (w: OrgWalletSummary) => boolean> = {
  ai_receipt_scanning: (w) => w.aiReceiptScanningEnabled,
  ai_statement_reconciliation: (w) => w.aiStatementReconciliationEnabled,
}

export const RECEIPT_BILLING = {
  feature: 'ai_receipt_scanning',
  permission: 'expenses.create',
} as const
export const STATEMENT_BILLING = {
  feature: 'ai_statement_reconciliation',
  permission: 'statements.access',
} as const

/**
 * The orgs eligible to pay for an AI action: ones where the user holds
 * `permission`, whose wallet has credit, AND that have `feature` enabled — so the
 * picker never offers an org the backend would reject.
 */
export function useEligibleBillingOrgs({
  feature,
  permission,
}: {
  feature: BillableAiFeature
  permission: string
}) {
  const { data: user } = useUser()
  const { data: wallets, isLoading } = useQuery(billingQueries.wallets())

  const eligibleMemberships = useMemo(() => {
    const isEnabled = featureFlag[feature]
    const eligibleOrgIds = new Set(
      (wallets ?? []).filter((w) => w.hasCredits && isEnabled(w)).map((w) => w.organizationId),
    )
    return (user?.memberships ?? []).filter(
      (m) => m.permissions.includes(permission) && eligibleOrgIds.has(m.organizationId),
    )
  }, [wallets, user, feature, permission])

  return { eligibleMemberships, canGenerate: eligibleMemberships.length > 0, isLoading }
}
